import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape GEO-06 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { ServiceArea } from "@tdelivr/api-client/generated/geo/service-area.js";

import {
  areaDetail,
  areaPage,
  boundaryText,
  boundedNote,
  coverageText,
  definitionOf,
  draftOf,
  servingNow,
  stateOf,
  stateReason,
  EMPTY_AREA_DRAFT,
  FILTERABLE_LIFECYCLES,
  PER_FETCH,
  VERSION_OF_NOTHING,
  type AreaDraft,
  type AreaLifecycle,
  type AreaOrder,
  type AreaProblem,
} from "../service-areas.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Service areas: where the platform operates, in the order it resolves them, and the form that
 * changes one.
 *
 * **No console design draws this page's navigation, let alone its body.** `service-areas` is in no
 * design file's `NAV`, which is why `navigation.ts` gives it the shell design's own fallback glyph
 * `circle` rather than a chosen pictogram (Q-07). The one board in the bundle that draws this
 * subject is `17 WP16 Tenant Admin.dc.html`, page `zones` — a *tenant-admin* screen, already built
 * as `apps/tenant-admin-console`'s `ZonesCoverage`. So this page is built to that file's board and
 * form template, value for value, with the dispatcher's question in it rather than the
 * administrator's. Recorded as Q-40.
 *
 * **The three data columns are the design's own: `Zone`, `Region`, `Coverage`.** `name`, `marketRef`
 * and `productRefs` are the sources. The design's three badge words — `served`, `partial`,
 * `review request` — are not GEO-06's, and `partial` has no field anywhere on the service; the
 * reasoning is in `stateOf` and the measurement in R-13.
 *
 * **An area stored `active` is not necessarily serving anybody, and this board does not say it is.**
 * GEO-06's availability evaluation requires the instant to be inside the effective window, and both
 * bounds are published on every row, so the screen can simply be right. On a dispatch console that
 * matters more than on an admin one: a dispatcher who believes a zone is open stops looking for the
 * reason work there is being refused.
 *
 * **The form writes, and its `expectedVersion` is the row's own.** `PUT /v1/service-areas/{id}` is
 * an upsert — GEO-06 reads an absent area's version as zero — so a new area is a client-minted
 * identifier at `expectedVersion: 0` and an amendment is the version the screen displayed. Either
 * way a race is refused with `service_area_version_conflict` rather than resolved by overwriting,
 * which is the designed 409 reached by a real one.
 */

const areasApi = services.geo__service_area;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: AreaOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

/** The scope every command on this page needs. */
const WRITE_SCOPE = "service-area:write";

export interface ServiceAreasProps {
  readonly transport: Transport;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** What this operator holds. Decides the view-only chip and whether the form's actions are live. */
  readonly scopes?: ReadonlySet<string>;
  /** Injected so the effective-window rule can be exercised without waiting for a date to pass. */
  readonly now?: () => number;
  /** Injected so a create can be driven without a random identifier in the request. See `mint`. */
  readonly newId?: () => string;
  /** Injected for the same reason, for the Idempotency-Key header every write carries. */
  readonly newKey?: () => string;
}

interface Loaded {
  readonly rows: readonly ServiceArea[];
  /** GEO-06 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

/** Which area the form is editing, or that it is making a new one. */
type FormTarget = { readonly kind: "new" } | { readonly kind: "edit"; readonly area: ServiceArea };

export function ServiceAreas({
  transport,
  crumbs,
  scopes,
  now,
  newId,
  newKey,
}: ServiceAreasProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>(undefined);
  const [lifecycle, setLifecycle] = useState<AreaLifecycle | undefined>(undefined);
  const [order, setOrder] = useState<AreaOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const [target, setTarget] = useState<FormTarget | undefined>(undefined);
  const [draft, setDraft] = useState<AreaDraft>(EMPTY_AREA_DRAFT);
  const [problems, setProblems] = useState<readonly AreaProblem[]>([]);
  const [refusal, setRefusal] = useState<ViewState | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const client = areasApi.createServiceAreaClient(transport);
    try {
      const answered = await client.getServiceAreas(lifecycle, PER_FETCH, undefined);
      setLoaded({
        rows: answered.areas,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["service-area:read"]));
      setLoaded(undefined);
    }
  }, [transport, lifecycle]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const instant = now === undefined ? Date.now() : now();

  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedArea = loaded?.rows.find((row) => row.id === selectedAreaId);

  const view = useMemo(
    () => areaPage(loaded?.rows ?? [], { search, order, page, perPage, now: instant }),
    [loaded, search, order, page, perPage, instant],
  );

  /*
   * No `observedAt`, so no stale state.
   *
   * GEO-06's listing publishes `updatedAt` per row and no page-level instant. A configuration board
   * is not a live board, so this is the least costly place that absence has been found; recorded in
   * R-13 all the same.
   */
  /*
   * A narrowed board stays `ready`, so the card can draw its own empty state.
   *
   * This read `stateForList(loaded?.rows, { filtered: lifecycle !== undefined })`, and that
   * stranded a control the design requires. Handed an empty list with `filtered: true`,
   * `stateForList` answers `empty/filtered` and `StateView` draws "Nothing matches this filter ·
   * Clear the filter to see everything on this page" **in place of its children** — so the
   * `search_off` block below, with the Clear filters button design rule 4 asks of every grid, was
   * never rendered. The sentence told the operator to clear a filter and gave them nothing to clear
   * it with. Reachable in one click: pick a lifecycle that answers no rows.
   *
   * The design has no such split. Its `grid.empty` block sits inside the card, above the pager,
   * whenever the row count is zero for any reason. So `stateForList` is consulted for the
   * unfiltered cases only, and its empty state is kept for the one case it is right for — a tenant
   * that genuinely has no rows, where there is no filter to clear and the sentence worth showing is
   * what would make one appear.
   *
   * Found by the agent that built the tenant admin console's five new boards, which hit the same
   * fault in the module copied from here and reported it against these screens rather than
   * changing them. The same two modules are copied into the finance console, which passes no
   * `filtered` flag at all — so its boards say "Nothing here yet" when a server-side filter is
   * what emptied them. That is a wrong sentence rather than a missing control, and is recorded.
   */
  const narrowed = lifecycle !== undefined || search.trim().length > 0;
  const viewState: ViewState =
    failure ??
    (narrowed && loaded?.rows !== undefined
      ? { kind: "ready", stale: false }
      : stateForList(loaded?.rows));
  const viewOnly = scopes !== undefined && !scopes.has(WRITE_SCOPE);

  const clearFilters = (): void => {
    setSearch("");
    setLifecycle(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    lifecycle,
    Number(PER_FETCH),
  );

  const openForm = (next: FormTarget): void => {
    setTarget(next);
    setDraft(next.kind === "edit" ? draftOf(next.area) : EMPTY_AREA_DRAFT);
    setProblems([]);
    setRefusal(undefined);
    setSaved(undefined);
  };

  const closeForm = (): void => {
    setTarget(undefined);
    setProblems([]);
    setRefusal(undefined);
    setSaved(undefined);
  };

  const submit = async (): Promise<void> => {
    if (target === undefined) return;
    const made = definitionOf(
      draft,
      target.kind === "new" ? VERSION_OF_NOTHING : target.area.version,
    );
    if (Array.isArray(made)) {
      setProblems(made as readonly AreaProblem[]);
      setRefusal(undefined);
      return;
    }
    setProblems([]);
    setRefusal(undefined);
    setSaving(true);
    const client = areasApi.createServiceAreaClient(transport);
    const id = target.kind === "new" ? (newId === undefined ? mint() : newId()) : target.area.id;
    const idempotencyKey = newKey === undefined ? mint() : newKey();
    try {
      await client.putServiceAreas(
        id,
        made as Parameters<
          ReturnType<typeof areasApi.createServiceAreaClient>["putServiceAreas"]
        >[1],
        { idempotencyKey },
      );
      setSaved(
        target.kind === "new"
          ? "Saved. It now appears in the list, in its precedence order."
          : "Saved. The coverage now in force is the one above.",
      );
      await load();
    } catch (error) {
      /*
       * The 409, reached rather than imagined.
       *
       * Two different conflicts arrive here and the words have to separate them. An amendment
       * refused means somebody else changed the area first. A *create* refused means the identifier
       * this console minted already exists — one in 2^122, so in practice it means the create
       * already succeeded and this is a second attempt under a different key.
       */
      setRefusal(stateForError(error, [WRITE_SCOPE]));
    } finally {
      setSaving(false);
    }
  };

  const problemFor = (field: keyof AreaDraft): string | undefined =>
    problems.find((problem) => problem.field === field)?.message;

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="areas__head">
      <span>Zone</span>
      <span>Region</span>
      <span>Coverage</span>
      <span className="areas__head--right">State</span>
    </div>
  );

  return (
    <section className="page areas">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Service areas"
        source="GEO-06"
        description="See and edit where the platform operates."
        {...(viewOnly
          ? {
              aside: (
                <span className="areas__role-note">
                  <span aria-hidden="true" className="areas__role-glyph">
                    visibility
                  </span>
                  View only · service-area:read
                </span>
              ),
            }
          : {})}
        action={
          <button
            type="button"
            className="areas__primary"
            disabled={viewOnly}
            {...(viewOnly ? { title: `Editing coverage needs ${WRITE_SCOPE}.` } : {})}
            onClick={() => openForm({ kind: "new" })}
          >
            <span aria-hidden="true" className="areas__primary-glyph">
              add
            </span>
            Add zone
          </button>
        }
      />

      {/*
        No tiles, where the design has four.

        `Zones · 34`, `Partial · 5`, `Not served · 3` and `Requests · 18` are three counts over a
        coverage vocabulary the contract does not have and one count of customer requests for new
        areas, which nothing on the platform records. The first could be a count of the rows this
        page holds, and R-04 is why it is not one: no listing publishes a total, so a tile reading 34
        would mean "34 so far" under a label that means "34". R-13.
      */}

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        `.detail` is docked two ways and both are written against `.board-split`: at 1240 and
        above `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the
        panel is `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s
        own `position: relative`. This board had no such wrapper, so an opened row drew a
        full-width block stacked under the table at 1440, and below 1240 an overlay anchored to
        the page rather than to the board. `DemandQueue.tsx` carries the measurements that found
        it: the first time any of these six panels was photographed, on 1 November.

        Which docking is right for these six at all is Q-297 — `17 WP16 Tenant Admin` draws
        a centred modal where `04 Dispatch Exceptions Board` draws this column, and that is a
        decision rather than a defect. The wrapper is not that decision: it is what makes the
        build do what its own stylesheet already says.
      */}
      <div className="board-split">
        <div className="areas__panel">
          <div className="areas__toolbar">
            <span className="areas__search">
              <span aria-hidden="true" className="areas__search-glyph">
                search
              </span>
              <input
                id="service-areas-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="service-areas-lifecycle"
              aria-label="Filter by state"
              value={lifecycle ?? ""}
              onChange={(event) => {
                setLifecycle(
                  event.target.value === "" ? undefined : (event.target.value as AreaLifecycle),
                );
                setPage(1);
              }}
            >
              {/*
              The design's word for the unfiltered option, over GEO-06's own three lifecycles rather
              than over the states on screen. Choosing one refetches, because the listing publishes
              a `lifecycle` parameter. The options are the lifecycles rather than the badges' words:
              an `active` area outside its window appears under `active` and reads "no longer
              effective", which is the truth about it rather than a fourth option the service would
              refuse.
            */}
              <option value="">All states</option>
              {FILTERABLE_LIFECYCLES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              id="service-areas-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as AreaOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="areas__export" ref={exportRegion}>
              <button
                type="button"
                className="areas__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="areas__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="areas__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="areas__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format, instant);
                      }}
                    >
                      <span aria-hidden="true" className="areas__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={viewState}
            purpose="Service areas appear here as soon as one is configured for this tenant."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "areas__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="areas__rows">
              {view.rows.map((area) => {
                const shown = stateOf(area, instant);
                const reason = stateReason(area, instant);
                return (
                  <div
                    key={area.id}
                    className={
                      area.lifecycle === "active" && !servingNow(area, instant)
                        ? "areas__row areas__row--cross"
                        : "areas__row"
                    }
                    role="button"
                    tabIndex={0}
                    data-selected={area.id === selectedAreaId ? "true" : undefined}
                    aria-label={viewOnly ? `Open service area ${area.id}` : `Edit ${area.name}`}
                    onClick={
                      viewOnly
                        ? (): void => setSelectedAreaId(area.id)
                        : (): void => openForm({ kind: "edit", area })
                    }
                    onKeyDown={(event): void => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      if (viewOnly) setSelectedAreaId(area.id);
                      else openForm({ kind: "edit", area });
                    }}
                  >
                    <span className="areas__name">{area.name}</span>
                    <span className="areas__region">{area.marketRef}</span>
                    <span className="areas__coverage">{coverageText(area)}</span>
                    <span className="areas__state">
                      <StateBadge
                        code="GEO-06"
                        status={area.lifecycle}
                        classKey={shown.classKey}
                        label={shown.label}
                        title={
                          reason ??
                          `Priority ${area.priority}, version ${area.version}. Boundary: ${boundaryText(area)}.`
                        }
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="areas__no-match">
                <span aria-hidden="true" className="areas__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="areas__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="areas__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="areas__pager-controls">
                <select
                  id="service-areas-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="areas__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {bounded === undefined ? null : (
            <p className="areas__bounded" role="status">
              {bounded}
            </p>
          )}
        </div>

        {/*
        The selected row, for an operator who may not edit. A writer's click opens the form
        instead — one row, two meanings, decided by what the operator may actually do. The panel is
        the `view only` half of a board whose rows were previously inert for exactly those
        operators.
      */}
        {selectedArea === undefined ? null : (
          <RecordDetail
            record={areaDetail(selectedArea, instant)}
            open
            onClose={() => setSelectedAreaId(undefined)}
          />
        )}
      </div>

      {/* The design's closing note for this page, word for word. */}
      <div className="areas__note">
        <span aria-hidden="true" className="areas__note-glyph">
          warning
        </span>
        <span>
          Coverage read responses are not published yet (G-16); the website check uses the same
          data.
        </span>
      </div>

      {target === undefined ? null : (
        <div className="areas__overlay" onClick={closeForm} role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={target.kind === "new" ? "Add a zone" : `Edit · ${target.area.name}`}
            className="areas__dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="areas__dialog-head">
              <span aria-hidden="true" className="areas__dialog-glyph">
                map
              </span>
              <span className="areas__dialog-title">
                <strong>
                  {target.kind === "new" ? "Add a zone" : `Edit · ${target.area.name}`}
                </strong>
                <span className="areas__dialog-sub">
                  {target.kind === "new"
                    ? "Fields marked * are required."
                    : `GEO-06 · version ${target.area.version}. An amendment carries this version.`}
                </span>
              </span>
              <button
                type="button"
                aria-label="Close"
                className="areas__dialog-close"
                onClick={closeForm}
              >
                close
              </button>
            </div>

            <div className="areas__dialog-body">
              {saved === undefined ? null : (
                <div role="status" className="areas__dialog-done">
                  <span aria-hidden="true" className="areas__dialog-done-glyph">
                    check_circle
                  </span>
                  <span>{saved}</span>
                </div>
              )}
              {refusal === undefined ? null : (
                <div
                  role="alert"
                  className={
                    refusal.kind === "conflict" ? "areas__dialog-conflict" : "areas__dialog-error"
                  }
                >
                  <span aria-hidden="true" className="areas__dialog-error-glyph">
                    {refusal.kind === "conflict" ? "merge_type" : "error"}
                  </span>
                  <span>
                    {refusal.kind === "conflict" ? (
                      <>
                        Somebody changed this first. {refusal.message} Close this and open the row
                        again to amend the coverage now in force.
                      </>
                    ) : refusal.kind === "forbidden" ? (
                      <>
                        You do not have access to this. It needs {WRITE_SCOPE}. Ask whoever
                        administers your roles for that permission.
                      </>
                    ) : refusal.kind === "error" ? (
                      <>
                        {refusal.message} ({refusal.code})
                      </>
                    ) : (
                      "That did not work."
                    )}
                  </span>
                </div>
              )}
              {problems.length === 0 ? null : (
                <div role="alert" className="areas__dialog-error">
                  <span aria-hidden="true" className="areas__dialog-error-glyph">
                    error
                  </span>
                  <span>
                    Complete {problems.length} field{problems.length > 1 ? "s" : ""}:{" "}
                    {problems.map((problem) => problem.field).join(", ")}.
                  </span>
                </div>
              )}

              <fieldset className="areas__fieldset">
                <legend>Zone</legend>
                <div className="areas__field-grid">
                  <label className="areas__field">
                    <span className="areas__label">
                      Zone name<span className="areas__required">*</span>
                    </span>
                    <input
                      type="text"
                      value={draft.name}
                      placeholder="e.g. Ruaka"
                      aria-invalid={problemFor("name") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, name: event.target.value }))
                      }
                    />
                    {problemFor("name") === undefined ? null : (
                      <span className="areas__hint areas__hint--bad">{problemFor("name")}</span>
                    )}
                  </label>
                  {/*
                    The design draws `Region` as a select over five named markets — Nairobi, Kiambu,
                    Mombasa, Kampala, Dar es Salaam. GEO-06 publishes `marketRef` as an opaque
                    reference matching its own pattern and nothing enumerates the markets a tenant
                    has, so a select would be a list of five guesses. A text field over the
                    published pattern is the honest shape. R-19.
                  */}
                  <label className="areas__field">
                    <span className="areas__label">
                      Region<span className="areas__required">*</span>
                    </span>
                    <input
                      type="text"
                      value={draft.marketRef}
                      placeholder="e.g. KE-NBO"
                      aria-invalid={problemFor("marketRef") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, marketRef: event.target.value }))
                      }
                    />
                    {problemFor("marketRef") === undefined ? null : (
                      <span className="areas__hint areas__hint--bad">
                        {problemFor("marketRef")}
                      </span>
                    )}
                  </label>
                  <label className="areas__field">
                    <span className="areas__label">
                      Precedence<span className="areas__required">*</span>
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={draft.priority}
                      placeholder="0"
                      aria-invalid={problemFor("priority") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, priority: event.target.value }))
                      }
                    />
                    <span
                      className={
                        problemFor("priority") === undefined
                          ? "areas__hint"
                          : "areas__hint areas__hint--bad"
                      }
                    >
                      {problemFor("priority") ??
                        "Where areas overlap, the higher number wins. This board is ordered by it."}
                    </span>
                  </label>
                  <label className="areas__field">
                    <span className="areas__label">
                      State<span className="areas__required">*</span>
                    </span>
                    <select
                      value={draft.lifecycle}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          lifecycle: event.target.value as AreaLifecycle,
                        }))
                      }
                    >
                      {FILTERABLE_LIFECYCLES.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/*
                    `Services`, which the design draws as chips over four named products. The chips
                    need a list of this tenant's products and GEO-06 publishes none, so this is the
                    published reference list itself — one per line, deduplicated and sorted the way
                    GEO-06's own schema transforms it before storing.
                  */}
                  <label className="areas__field areas__field--wide">
                    <span className="areas__label">
                      Services<span className="areas__required">*</span>
                    </span>
                    <textarea
                      rows={3}
                      value={draft.productRefs}
                      placeholder={"economy\nboda\nsame-day-parcel"}
                      aria-invalid={problemFor("productRefs") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, productRefs: event.target.value }))
                      }
                    />
                    <span
                      className={
                        problemFor("productRefs") === undefined
                          ? "areas__hint"
                          : "areas__hint areas__hint--bad"
                      }
                    >
                      {problemFor("productRefs") ??
                        "One product reference per line. These are the products this area serves."}
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="areas__fieldset">
                <legend>When</legend>
                <div className="areas__field-grid">
                  <label className="areas__field">
                    <span className="areas__label">
                      Effective from<span className="areas__required">*</span>
                    </span>
                    <input
                      type="text"
                      value={draft.effectiveFrom}
                      placeholder="2026-10-01T00:00:00.000Z"
                      aria-invalid={problemFor("effectiveFrom") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, effectiveFrom: event.target.value }))
                      }
                    />
                    {problemFor("effectiveFrom") === undefined ? null : (
                      <span className="areas__hint areas__hint--bad">
                        {problemFor("effectiveFrom")}
                      </span>
                    )}
                  </label>
                  <label className="areas__field">
                    <span className="areas__label">Effective until</span>
                    <input
                      type="text"
                      value={draft.effectiveUntil}
                      placeholder="leave empty for no end"
                      aria-invalid={problemFor("effectiveUntil") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, effectiveUntil: event.target.value }))
                      }
                    />
                    <span
                      className={
                        problemFor("effectiveUntil") === undefined
                          ? "areas__hint"
                          : "areas__hint areas__hint--bad"
                      }
                    >
                      {problemFor("effectiveUntil") ??
                        "Outside this window an active area serves nobody, whatever its state says."}
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="areas__fieldset">
                <legend>Boundary</legend>
                <div className="areas__field-grid">
                  <label className="areas__field">
                    <span className="areas__label">
                      Defined by<span className="areas__required">*</span>
                    </span>
                    <select
                      value={draft.boundaryKind}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          boundaryKind: event.target.value as AreaDraft["boundaryKind"],
                        }))
                      }
                    >
                      <option value="geometry">Its own GeoJSON</option>
                      <option value="geofence">A GEO-05 geofence</option>
                    </select>
                  </label>
                  {draft.boundaryKind === "geofence" ? (
                    <>
                      <label className="areas__field">
                        <span className="areas__label">
                          Geofence<span className="areas__required">*</span>
                        </span>
                        <input
                          type="text"
                          value={draft.geofenceId}
                          placeholder="geofence id from the Geofences page"
                          aria-invalid={problemFor("geofenceId") !== undefined}
                          onChange={(event) =>
                            setDraft((current) => ({ ...current, geofenceId: event.target.value }))
                          }
                        />
                        {problemFor("geofenceId") === undefined ? null : (
                          <span className="areas__hint areas__hint--bad">
                            {problemFor("geofenceId")}
                          </span>
                        )}
                      </label>
                      <label className="areas__field">
                        <span className="areas__label">
                          Geofence version<span className="areas__required">*</span>
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={draft.geofenceVersion}
                          placeholder="1"
                          aria-invalid={problemFor("geofenceVersion") !== undefined}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              geofenceVersion: event.target.value,
                            }))
                          }
                        />
                        <span
                          className={
                            problemFor("geofenceVersion") === undefined
                              ? "areas__hint"
                              : "areas__hint areas__hint--bad"
                          }
                        >
                          {problemFor("geofenceVersion") ??
                            "Pinned, so amending the fence does not move this area."}
                        </span>
                      </label>
                    </>
                  ) : (
                    <label className="areas__field areas__field--wide">
                      <span className="areas__label">
                        GeoJSON<span className="areas__required">*</span>
                      </span>
                      <textarea
                        rows={6}
                        value={draft.geometry}
                        placeholder='{"type":"Polygon","coordinates":[[[36.80,-1.29],[36.84,-1.29],[36.84,-1.26],[36.80,-1.26],[36.80,-1.29]]]}'
                        aria-invalid={problemFor("geometry") !== undefined}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, geometry: event.target.value }))
                        }
                      />
                      <span
                        className={
                          problemFor("geometry") === undefined
                            ? "areas__hint"
                            : "areas__hint areas__hint--bad"
                        }
                      >
                        {problemFor("geometry") ??
                          "A GeoJSON Polygon or MultiPolygon. Positions are [longitude, latitude], and every ring repeats its first position last."}
                      </span>
                    </label>
                  )}
                </div>
              </fieldset>

              {/*
                The three fields the design draws and this service has nowhere to put, named rather
                than silently dropped. An operator who remembers the form having them is owed the
                reason it does not.
              */}
              <div className="areas__dialog-note">
                <span aria-hidden="true" className="areas__dialog-note-glyph">
                  info
                </span>
                <span>
                  Coverage, Hours and the reason shown to customers are not published by GEO-06, so
                  they cannot be saved here. An area either serves its listed products or is not
                  effective.
                </span>
              </div>
            </div>

            <div className="areas__dialog-foot">
              <button type="button" className="areas__dialog-cancel" onClick={closeForm}>
                Cancel
              </button>
              <button
                type="button"
                className="areas__dialog-save"
                disabled={saving || viewOnly}
                onClick={() => {
                  void submit();
                }}
              >
                {target.kind === "new" ? "Add zone" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * A new identifier, or an idempotency key.
 *
 * `randomUUID` where the origin is secure and a random hex string where it is not. GEO-06's path
 * parameter is parsed as a UUID, so the fallback is formatted as one rather than as bare hex; what
 * the fallback does not do is throw on a console served over plain HTTP, which is what
 * `crypto.randomUUID` does.
 */
function mint(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined") crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * lifecycle — including the effective-window correction, so an exported row says what the board did.
 */
function exportRows(rows: readonly ServiceArea[], format: ExportFormat, now: number): void {
  const header = ["Zone", "Region", "Coverage", "State", "Precedence", "Version"];
  const body = rows.map((area) => [
    area.name,
    area.marketRef,
    coverageText(area),
    stateOf(area, now).label,
    String(area.priority),
    String(area.version),
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "service-areas.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "service-areas.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>service-areas</title></head><body><h2 style="font-family:sans-serif">service-areas</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
